/**
 * Haptic Feedback Utility
 * Provides vibration feedback for touch interactions on mobile devices
 */

// Check if Vibration API is supported
const isVibrationSupported = (): boolean => {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Vibration patterns (in milliseconds)
export const HapticPatterns = {
    // Light tap - for regular button clicks
    light: [10],

    // Medium tap - for important actions
    medium: [25],

    // Heavy tap - for significant actions (submit, confirm)
    heavy: [50],

    // Success pattern - double vibration
    success: [30, 50, 30],

    // Error pattern - triple short vibrations
    error: [50, 30, 50, 30, 50],

    // Warning pattern
    warning: [40, 50, 40],

    // Notification pattern
    notification: [20, 100, 20],

    // Selection change
    selection: [8],

    // Long press feedback
    longPress: [15, 30, 60],

    // Swipe feedback
    swipe: [5, 20, 5],
} as const;

export type HapticPattern = keyof typeof HapticPatterns;

// Default pattern used throughout the app
const DEFAULT_HAPTIC_PATTERN: HapticPattern = 'light';

/**
 * Get the user's preferred haptic pattern
 */
export const getHapticPatternPreference = (): HapticPattern => {
    const stored = localStorage.getItem('haptic_pattern_preference');
    if (stored && stored in HapticPatterns) {
        return stored as HapticPattern;
    }
    return DEFAULT_HAPTIC_PATTERN;
};

/**
 * Set the user's preferred haptic pattern
 */
export const setHapticPatternPreference = (pattern: HapticPattern): void => {
    localStorage.setItem('haptic_pattern_preference', pattern);
    // Trigger the selected pattern to demo it
    triggerHapticRaw(pattern);
};

/**
 * Internal function that directly triggers vibration (no preference lookup to avoid recursion)
 */
const triggerHapticRaw = (pattern: HapticPattern): boolean => {
    if (!isVibrationSupported()) {
        return false;
    }

    const hapticEnabled = localStorage.getItem('haptic_feedback_enabled');
    if (hapticEnabled === 'false') {
        return false;
    }

    try {
        const vibrationPattern = [...HapticPatterns[pattern]];
        navigator.vibrate(vibrationPattern);
        return true;
    } catch (error) {
        console.warn('Haptic feedback failed:', error);
        return false;
    }
};

/**
 * Trigger haptic feedback
 * @param pattern - The vibration pattern to use
 * @returns boolean - Whether the vibration was triggered
 */
export const triggerHaptic = (pattern?: HapticPattern): boolean => {
    // If no pattern specified, use user's preferred pattern
    const effectivePattern = pattern || getHapticPatternPreference();
    return triggerHapticRaw(effectivePattern);
};

/**
 * Stop any ongoing vibration
 */
export const stopHaptic = (): void => {
    if (isVibrationSupported()) {
        navigator.vibrate(0);
    }
};

/**
 * Check if haptic feedback is available on this device
 */
export const isHapticAvailable = (): boolean => {
    return isVibrationSupported();
};

/**
 * Get/Set haptic feedback preference
 */
export const getHapticPreference = (): boolean => {
    const stored = localStorage.getItem('haptic_feedback_enabled');
    // Default to true if not set
    return stored !== 'false';
};

export const setHapticPreference = (enabled: boolean): void => {
    localStorage.setItem('haptic_feedback_enabled', String(enabled));

    // Trigger a test vibration if enabling
    if (enabled && isVibrationSupported()) {
        triggerHaptic('light');
    }
};

/**
 * Create an onClick handler with haptic feedback
 * @param handler - The original onClick handler
 * @param pattern - The haptic pattern to use
 */
export const withHaptic = <T extends (...args: any[]) => any>(
    handler: T,
    pattern: HapticPattern = 'light'
): T => {
    return ((...args: Parameters<T>) => {
        triggerHaptic(pattern);
        return handler(...args);
    }) as T;
};

/**
 * Higher-order function to wrap any callback with haptic feedback
 */
export const hapticCallback = <T extends (...args: any[]) => any>(
    callback: T,
    pattern: HapticPattern = 'light'
): ((...args: Parameters<T>) => ReturnType<T>) => {
    return (...args: Parameters<T>): ReturnType<T> => {
        triggerHaptic(pattern);
        return callback(...args);
    };
};
